import { useCallback, useEffect, useMemo, useState } from 'react';
import { PROFILE_SORT_ORDER_OPTIONS, PROFILE_SORTABLE_COLUMNS } from '../../../config/enum-options.js';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import { useFilterPresets } from '../../../shared/filter-presets/use-filter-presets.js';
import {
  ADMIN_NOTIFICATION_TYPES,
  useAdminNotificationRefresh,
} from '../../../shared/realtime/admin-notification-events.js';
import { buildProfilesRequestDto, mapProfilesResponseToDto } from '../dto/profile-list.dto.js';
import { buildProfilesTableModel } from '../model/profile-list.model.js';

const DEFAULT_FILTERS = Object.freeze({
  firstName: '',
  lastName: '',
  email: '',
  countriesOnly: [],
  status: '',
  assignedOnly: false,
  showInvalidated: false,
});

const DEFAULT_SORT = Object.freeze({
  field: PROFILE_SORTABLE_COLUMNS.created,
  order: PROFILE_SORT_ORDER_OPTIONS.desc,
});

const PROFILE_PAGE_SIZE = 20;
const PROFILE_FILTER_STORAGE_KEY = 'admin_profiles_list_filters';

function readSearchParams() {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
}

function readStringParam(params, key) {
  return String(params.get(key) || '').trim();
}

function readBooleanParam(params, key) {
  const value = String(params.get(key) || '').trim().toLowerCase();
  return value === '1' || value === 'true';
}

function getInitialFiltersFromUrl() {
  const params = readSearchParams();

  return {
    firstName: readStringParam(params, 'firstName'),
    lastName: readStringParam(params, 'lastName'),
    email: readStringParam(params, 'email'),
    countriesOnly: params.getAll('country').map((item) => String(item || '').trim().toUpperCase()).filter(Boolean),
    status: readStringParam(params, 'status'),
    assignedOnly: readBooleanParam(params, 'assignedOnly'),
    showInvalidated: readBooleanParam(params, 'showInvalidated'),
  };
}

function readStoredFilters() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(PROFILE_FILTER_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function hasActiveFilters(filters) {
  return Boolean(
    filters.firstName
      || filters.lastName
      || filters.email
      || filters.status
      || filters.assignedOnly
      || filters.showInvalidated
      || filters.countriesOnly.length > 0,
  );
}

function getInitialProfileFilters() {
  const urlFilters = normalizeProfileFilters(getInitialFiltersFromUrl());
  if (hasActiveFilters(urlFilters)) {
    return urlFilters;
  }

  const storedFilters = readStoredFilters();
  return normalizeProfileFilters(storedFilters || DEFAULT_FILTERS);
}

function normalizeProfileFilters(filters) {
  return {
    ...DEFAULT_FILTERS,
    ...filters,
    firstName: String(filters?.firstName || '').trim(),
    lastName: String(filters?.lastName || '').trim(),
    email: String(filters?.email || '').trim(),
    countriesOnly: Array.isArray(filters?.countriesOnly)
      ? filters.countriesOnly.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
      : [],
    status: String(filters?.status || '').trim(),
    assignedOnly: filters?.assignedOnly === true,
    showInvalidated: filters?.showInvalidated === true,
  };
}

function buildFilterSearchParams(filters) {
  const normalized = normalizeProfileFilters(filters);
  const params = new URLSearchParams();

  if (normalized.firstName) {
    params.set('firstName', normalized.firstName);
  }
  if (normalized.lastName) {
    params.set('lastName', normalized.lastName);
  }
  if (normalized.email) {
    params.set('email', normalized.email);
  }
  if (normalized.status) {
    params.set('status', normalized.status);
  }
  normalized.countriesOnly.forEach((country) => {
    params.append('country', country);
  });
  if (normalized.assignedOnly) {
    params.set('assignedOnly', '1');
  }
  if (normalized.showInvalidated) {
    params.set('showInvalidated', '1');
  }

  return params;
}

function persistProfileFilters(filters) {
  if (typeof window === 'undefined') {
    return;
  }

  const normalized = normalizeProfileFilters(filters);
  const active = hasActiveFilters(normalized);

  try {
    if (active) {
      window.sessionStorage.setItem(PROFILE_FILTER_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      window.sessionStorage.removeItem(PROFILE_FILTER_STORAGE_KEY);
    }
  } catch {
    // ignore unavailable storage
  }

  const params = buildFilterSearchParams(normalized);
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function mapAdminProfileOptions(payload) {
  return normalizeList(payload)
    .map((profile, index) => {
      const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();
      if (!id) {
        return null;
      }

      const firstName = pickFirst(profile?.firstName, profile?.first_name);
      const lastName = pickFirst(profile?.lastName, profile?.last_name);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallbackName = pickFirst(
        profile?.name,
        profile?.full_name,
        profile?.username,
        profile?.emailMasked,
        profile?.email_masked,
        `Profile ${index + 1}`,
      );
      const label = fullName || String(fallbackName || '').trim() || id;
      return { id, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);

  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return { value, label: name ? `${value} - ${name}` : value };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((a, b) => a.value.localeCompare(b.value));
}

export function useProfilesList() {
  const { token } = useAuth();
  const initialFilters = useMemo(() => getInitialProfileFilters(), []);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [isFilterOpen, setIsFilterOpen] = useState(() => hasActiveFilters(initialFilters));
  const [rows, setRows] = useState([]);
  const [hasMoreProfiles, setHasMoreProfiles] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTargetProfileId, setAssignTargetProfileId] = useState('');
  const [assignTargetProfileName, setAssignTargetProfileName] = useState('');
  const [adminProfileOptions, setAdminProfileOptions] = useState([]);
  const [isAdminProfilesLoading, setIsAdminProfilesLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');

  const applyPresetFilters = useCallback((presetFilters) => {
    setFilters(normalizeProfileFilters(presetFilters));
    setIsFilterOpen(true);
  }, []);

  const filterPresetState = useFilterPresets({
    storageKey: 'admin_profile_filter_presets',
    filters,
    onApply: applyPresetFilters,
  });

  useEffect(() => {
    persistProfileFilters(filters);
  }, [filters]);

  const loadProfilesPage = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError('');

    try {
      const payload = buildProfilesRequestDto(filters, sort, {
        limit: PROFILE_PAGE_SIZE,
        offset,
      });
      const response = await adminApi.getProfiles(token, payload);
      const dto = mapProfilesResponseToDto(normalizeList(response));
      const nextRows = buildProfilesTableModel(dto);
      setRows((previousRows) => (append ? [...previousRows, ...nextRows] : nextRows));
      setHasMoreProfiles(nextRows.length === PROFILE_PAGE_SIZE);
    } catch (requestError) {
      if (!append) {
        setRows([]);
        setHasMoreProfiles(false);
      }
      setError(requestError.message || 'Failed to load profiles');
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [token, filters, sort]);

  useEffect(() => {
    loadProfilesPage({ offset: 0, append: false });
  }, [loadProfilesPage]);

  useAdminNotificationRefresh(ADMIN_NOTIFICATION_TYPES.profileCreated, () => {
    loadProfilesPage({ offset: 0, append: false });
  });

  const loadMoreProfiles = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMoreProfiles) {
      return;
    }

    loadProfilesPage({ offset: rows.length, append: true });
  }, [hasMoreProfiles, isLoading, isLoadingMore, loadProfilesPage, rows.length]);

  useEffect(() => {
    let isCancelled = false;

    const loadCountries = async () => {
      try {
        const response = await adminApi.getCountryDictionary(token);
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      }
    };

    loadCountries();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const sortIndicator = useMemo(
    () => (field) => {
      if (sort.field !== field) {
        return 'fa fa-sort text-muted';
      }

      return sort.order === PROFILE_SORT_ORDER_OPTIONS.asc ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    [sort],
  );

  const toggleSort = useCallback((field) => {
    if (!field) {
      return;
    }

    setSort((previous) => {
      if (previous.field !== field) {
        return { field, order: PROFILE_SORT_ORDER_OPTIONS.asc };
      }

      return {
        field,
        order:
          previous.order === PROFILE_SORT_ORDER_OPTIONS.asc
            ? PROFILE_SORT_ORDER_OPTIONS.desc
            : PROFILE_SORT_ORDER_OPTIONS.asc,
      };
    });
  }, []);

  const updateFilter = useCallback((event) => {
    const { name, value, type, checked } = event.target;
    setFilters((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const resetAndCloseFilters = useCallback(() => {
    setFilters(normalizeProfileFilters(DEFAULT_FILTERS));
    setIsFilterOpen(false);
  }, []);

  const loadAdminProfiles = useCallback(async () => {
    setIsAdminProfilesLoading(true);
    setAssignmentError('');

    try {
      const response = await adminApi.getProfiles(token, { isAdmin: true, limit: 100 });
      setAdminProfileOptions(mapAdminProfileOptions(response));
    } catch (requestError) {
      setAdminProfileOptions([]);
      setAssignmentError(requestError.message || 'Failed to load admin profiles');
    } finally {
      setIsAdminProfilesLoading(false);
    }
  }, [token]);

  const openAssignModal = useCallback(async (row) => {
    const profileId = String(row?.id || '').trim();
    if (!profileId) {
      return;
    }

    setAssignTargetProfileId(profileId);
    setAssignTargetProfileName(String(row?.name || '').trim() || '-');
    setSelectedManagerId(String(row?.assignedToId || '').trim());
    setAssignmentError('');
    setIsAssignModalOpen(true);
    await loadAdminProfiles();
  }, [loadAdminProfiles]);

  const closeAssignModal = useCallback((force = false) => {
    if (isAssignmentSaving && !force) {
      return;
    }
    setIsAssignModalOpen(false);
    setAssignTargetProfileId('');
    setAssignTargetProfileName('');
    setSelectedManagerId('');
    setAssignmentError('');
  }, [isAssignmentSaving]);

  const assignManager = useCallback(async () => {
    if (!assignTargetProfileId) {
      setAssignmentError('Profile id is required');
      return;
    }

    const managerId = String(selectedManagerId || '').trim();
    if (!managerId) {
      setAssignmentError('Select a profile to assign');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.assignProfile(token, assignTargetProfileId, managerId);
      closeAssignModal(true);
      await loadProfilesPage({ offset: 0, append: false });
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to assign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  }, [assignTargetProfileId, selectedManagerId, token, closeAssignModal, loadProfilesPage]);

  const unassignManager = useCallback(async () => {
    if (!assignTargetProfileId) {
      setAssignmentError('Profile id is required');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.unassignProfile(token, assignTargetProfileId);
      closeAssignModal(true);
      await loadProfilesPage({ offset: 0, append: false });
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to unassign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  }, [assignTargetProfileId, token, closeAssignModal, loadProfilesPage]);

  return {
    rows,
    countryOptions,
    filters,
    sort,
    isFilterOpen,
    isLoading,
    isLoadingMore,
    hasMoreProfiles,
    error,
    toggleSort,
    updateFilter,
    resetAndCloseFilters,
    setIsFilterOpen,
    isAssignModalOpen,
    assignTargetProfileName,
    adminProfileOptions,
    isAdminProfilesLoading,
    selectedManagerId,
    isAssignmentSaving,
    assignmentError,
    openAssignModal,
    closeAssignModal,
    onSelectManager: (managerId) => setSelectedManagerId(String(managerId || '').trim()),
    onCountriesFilterChange: (countries) => setFilters((prev) => ({
      ...prev,
      countriesOnly: Array.isArray(countries)
        ? countries.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean)
        : [],
    })),
    assignManager,
    unassignManager,
    sortIndicator,
    loadMoreProfiles,
    ...filterPresetState,
    reload: () => loadProfilesPage({ offset: 0, append: false }),
  };
}
